// src/components/result/SpirometryResultPreview.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ResultPreview } from "./ResultPreview";
import LoadingBouncyDots from "../ui/LoadingBouncyDots";
import MetricLineChart from "../charts/MetricLineChart";
import { useAccessControl } from "../../hooks/useAccessControl";
import {
  monthKeyLocal,
  getRedemptionForMonth,
  redeemFreeLungFitness,
} from "../../utils/firebase/freemiumRedemptions";
import { useUserDoc } from "../../hooks/useUserDoc";
import { smoothSeriesBySeconds } from "../../utils/results/smoothing";

export function SpirometryResultPreview({
  activityId,
  date,
  data: dataFromProps,
  locked, // API compat
  loading = false, // from parent
}) {
  const { t } = useTranslation();
  const { rules, plan } = useAccessControl();
  const { user } = useUserDoc();

  const loadingFromProps = loading;
  const [redeemedThisCard, setRedeemedThisCard] = useState(false);
  const [redemptionStatus, setRedemptionStatus] = useState("loading"); // 'loading' | 'none' | 'this' | 'other'
  const monthKey = useMemo(() => monthKeyLocal(date, "Europe/London"), [date]);
  const isFree = plan === "free";

  // Determine this month's redemption status for Free
  useEffect(() => {
    let cancelled = false;
    if (!user?.uid || !isFree || !activityId) {
      setRedemptionStatus("none");
      return;
    }
    (async () => {
      const res = await getRedemptionForMonth(user.uid, monthKey);
      if (cancelled) return;
      if (!res.exists) {
        setRedemptionStatus("none");
      } else if (res.activityId && res.activityId === activityId) {
        setRedemptionStatus("this");
        setRedeemedThisCard(true);
      } else {
        setRedemptionStatus("other");
      }
    })().catch(() => !cancelled && setRedemptionStatus("none"));
    return () => {
      cancelled = true;
    };
  }, [user?.uid, isFree, activityId, monthKey]);

  const effectiveData = dataFromProps ?? null;

  // Build chart points (smoothed) as soon as data exists
  const flowDataset = useMemo(() => {
    const xs = Array.isArray(effectiveData?.times) ? effectiveData.times : null;
    const traceArray = Array.isArray(effectiveData?.best_traces?.[0])
      ? effectiveData.best_traces[0]
      : Array.isArray(effectiveData?.trace)
      ? effectiveData.trace
      : null;

    if (!xs || !traceArray || xs.length === 0 || traceArray.length === 0)
      return null;

    const n = Math.min(xs.length, traceArray.length);

    // Smooth ~0.6s window (tweakable)
    const SMOOTH_SEC = 1;
    const smoothed = smoothSeriesBySeconds(
      xs.slice(0, n),
      traceArray.slice(0, n),
      SMOOTH_SEC
    );

    return {
      label: t("activity.spirometry.flowLabel", {
        defaultValue: "Breath Trace",
      }),
      data: smoothed, // {x,y} points
      tension: 0.35, // a touch more bezier smoothing in Chart.js
      borderWidth: 3,
      unit: t("activity.spirometry.flowUnit", { defaultValue: "L/s" }),
    };
  }, [effectiveData, t]);

  // Decide chart loading overlay: only while there are NO points yet
  const chartHasPoints = !!(flowDataset?.data && flowDataset.data.length > 0);
  const chartLoading = !chartHasPoints && loadingFromProps;

  // Stats: placeholder vs real
  const dot = (
    <div className="p-1">
      <LoadingBouncyDots
        text=""
        size={4}
        gap={1}
        speed={720}
        className="items-start"
        textClassName="hidden"
      />
    </div>
  );

  const stats = useMemo(() => {
    if (!effectiveData) {
      return [
        // {
        //   label: t("activity.spirometry.stat1", { defaultValue: "Lung Age" }),
        //   value: dot,
        //   hint: t("common.processing", { defaultValue: "Processing" }),
        //   metric: "lungAge",
        // },
        {
          label: t("activity.spirometry.stat2", { defaultValue: "Lung Score" }),
          value: dot,
          hint: t("common.processing", { defaultValue: "Processing" }),
          metric: "lungFitness",
        },
        {
          label: t("activity.spirometry.stat3", { defaultValue: "Resp. Rate" }),
          value: dot,
          hint: t("common.processing", { defaultValue: "Processing" }),
        },
      ];
    }

    const d = effectiveData || {};
    return [
      // {
      //   label: t("activity.spirometry.stat1", { defaultValue: "Lung Age" }),
      //   value: Number.isFinite(d?.lung_age_years)
      //     ? String(d.lung_age_years)
      //     : dot,
      //   hint: t("activity.spirometry.stat1hint", { defaultValue: "years" }),
      //   metric: "lungAge",
      // },
      {
        label: t("activity.spirometry.stat2", { defaultValue: "Lung Score" }),
        value: Number.isFinite(d?.ratio)
          ? String(Math.round(d.ratio * 100))
          : dot,
        hint: t("activity.spirometry.stat2hint", { defaultValue: "0-100" }),
        metric: "lungFitness",
      },
      {
        label: t("activity.spirometry.stat3", { defaultValue: "Resp. Rate" }),
        value:
          Number.isFinite(d?.rr_median_ipi) && d.rr_median_ipi > 0
            ? String(Math.round(d.rr_median_ipi))
            : dot,
        hint: t("activity.spirometry.stat3hint", {
          defaultValue: "breaths/min",
        }),
        metric: "respiratoryRate",
      },
    ];
  }, [chartHasPoints, effectiveData, t]);

  // Locks incl. free redemption override
  const mergedLocks = useMemo(() => {
    const base = rules?.locks ?? {};
    if (!isFree) return base;
    if (redeemedThisCard || redemptionStatus === "this") {
      return {
        ...base,
        lungAge: false,
        lungFitness: false,
        breathingTrace: false,
      };
    }
    return base;
  }, [rules, isFree, redeemedThisCard, redemptionStatus]);

  const handleUnlock = useCallback(async () => {
    try {
      const res = await getRedemptionForMonth(user?.uid, monthKey);
      if (res.exists) {
        setRedemptionStatus(res.activityId === activityId ? "this" : "other");
        return;
      }
      await redeemFreeLungFitness({
        uid: user?.uid,
        monthKey,
        activityId: activityId ?? effectiveData?.activityId ?? null,
        resultRef: effectiveData?.resultRef ?? null,
        activityDateMs:
          effectiveData?.activityDateMs ??
          (date ? new Date(date).getTime() : null),
      });
      setRedeemedThisCard(true);
      setRedemptionStatus("this");
    } catch (err) {
      console.error(err);
    }
  }, [monthKey, date, effectiveData, user?.uid, activityId]);

  // Chart node
  const chartNode = useMemo(() => {
    if (!flowDataset) return null;

    const xVals = (flowDataset.data ?? []).map((p) => p.x);
    const dataMaxX = xVals.length ? Math.max(...xVals) : 0;
    const xMax = Math.max(60, Math.ceil(dataMaxX));

    return (
      <MetricLineChart
        height={160}
        datasets={[flowDataset]}
        preset="none"
        showLegend={false}
        showTooltip={false}
        xType="linear"
        xMin={0}
        xMax={xMax}
        xTickStep={15}
        xTickFormat={(v) => `${v}s`}
        loading={chartLoading}
        showYTickLabels={false} // <— hide Y numbers, keep gridlines
        xLabel={t("activity.spirometry.timeLabel", {
          defaultValue: "Time (s)",
        })}
      />
    );
  }, [flowDataset, chartLoading, t]);

  const isLockedForThisCard =
    isFree &&
    (mergedLocks?.breathingTrace ||
      mergedLocks?.lungAge ||
      mergedLocks?.lungFitness);

  const overlayCTA =
    redemptionStatus === "none"
      ? {
          label: t("pay.free.unlockLungFitness", {
            defaultValue: "Use my free monthly Lung Fitness",
          }),
          ariaLabel: t("pay.free.unlockLungFitnessAria", {
            defaultValue: "Unlock this month's free Lung Fitness & Age",
          }),
          onClick: handleUnlock,
          to: undefined,
        }
      : redemptionStatus === "other"
      ? {
          label: t("pay.upgradeUnlock", { defaultValue: "Upgrade to unlock" }),
          ariaLabel: t("pay.upgradeUnlockAria", {
            defaultValue: "Upgrade to unlock all results",
          }),
          onClick: undefined,
          to: "/payment",
        }
      : null;

  return (
    <ResultPreview
      recording={"spirometry"}
      activityDate={date}
      stats={stats}
      locks={mergedLocks}
      showLockedUI={isLockedForThisCard && !!overlayCTA}
      lockProps={{}}
      cta={overlayCTA || undefined}
      titleKey="activity.spirometry.title"
      titleDefault="Lung Fitness & Age Test"
      chartTitle={t("activity.spirometry.chartTitle", {
        defaultValue: "Breathing Trace",
      })}
      chart={chartNode}
      infoKey="info.breathingTrace"
      onUnlock={overlayCTA?.onClick}
    />
  );
}
